import { getBoggleBadgeDefinition } from '~/boggle/config/badges/registry'
import type { BoggleBadgeDefinition, BoggleBadgeLevel } from '~/boggle/config/badges/types'
import type { BoggleEarnedBadge } from '~/boggle/lib/badges/evaluateRunBadges'

export type BoggleRunSummaryRatingId =
  | 'ace'
  | 'bronze'
  | 'diamond'
  | 'gold'
  | 'none'
  | 'silver'

export type BoggleRunSummaryTierLevel = 'I' | 'II' | 'III' | 'IV' | 'V'

export type BoggleRunSummaryTierCategoryId = 'pathfinder' | 'scholar' | 'wordsmith'

export type BoggleRunSummaryCoreLineId = 'gold-tiles-used' | 'tiles-cleared'

export type BoggleRunSummaryCoreLineConfig = {
  id: BoggleRunSummaryCoreLineId
  label: string
  pointsPerUnit: number
}

export type BoggleRunSummaryTierConfig = {
  level: BoggleRunSummaryTierLevel
  minInclusive: number
  points: number
}

export type BoggleRunSummaryTierCategoryConfig = {
  id: BoggleRunSummaryTierCategoryId
  label: string
  meterMinInclusive?: number
  metricLabel: string
  tiers: readonly BoggleRunSummaryTierConfig[]
}

export type BoggleRunSummaryRatingConfig = {
  id: BoggleRunSummaryRatingId
  label: string
  minInclusive: number
}

export type BoggleRunSummaryScoreInput = {
  /** Badges earned during the run, in unlock order. Each contributes its
   *  authored point value after tier bonuses. Empty when no badges fired. */
  earnedBadges?: readonly BoggleEarnedBadge[]
  goldTilesUsed: number
  scoredWords: readonly string[]
  tilesCleared: number
}

export type BoggleRunSummaryCoreLine = BoggleRunSummaryCoreLineConfig & {
  points: number
  value: number
}

export type BoggleRunSummaryTierLine = {
  category: BoggleRunSummaryTierCategoryConfig
  metricValue: number
  points: number
  tier: BoggleRunSummaryTierConfig | null
}

/** A single earned-badge award rendered after tier bonuses. */
export type BoggleRunSummaryBadgeLine = {
  badge: BoggleBadgeDefinition
  earnedAtLedgerIndex: number
  earnedAtMs: number
  points: number
}

/** Common first, legendary last — matches Compendium section order. */
const BADGE_LEVEL_SCORING_ORDER: readonly BoggleBadgeLevel[] = [
  'common',
  'uncommon',
  'rare',
  'legendary',
  'impossible',
]

const BADGE_LEVEL_SCORING_RANK = Object.fromEntries(
  BADGE_LEVEL_SCORING_ORDER.map((level, index) => [level, index]),
) as Record<BoggleBadgeLevel, number>

function compareBadgeLinesForScoring(
  left: BoggleRunSummaryBadgeLine,
  right: BoggleRunSummaryBadgeLine,
): number {
  const rarityDelta =
    BADGE_LEVEL_SCORING_RANK[left.badge.level] -
    BADGE_LEVEL_SCORING_RANK[right.badge.level]
  if (rarityDelta !== 0) {
    return rarityDelta
  }
  const ledgerDelta = left.earnedAtLedgerIndex - right.earnedAtLedgerIndex
  if (ledgerDelta !== 0) {
    return ledgerDelta
  }
  return left.earnedAtMs - right.earnedAtMs
}

/** Same ordering as post-run `badgeLines` reveal (rarity ascending, then unlock order). */
export function compareEarnedBadgesForSummaryDisplay(
  left: BoggleEarnedBadge,
  right: BoggleEarnedBadge,
): number {
  const leftBadge = getBoggleBadgeDefinition(left.badgeId)
  const rightBadge = getBoggleBadgeDefinition(right.badgeId)
  if (!leftBadge && !rightBadge) {
    return 0
  }
  if (!leftBadge) {
    return 1
  }
  if (!rightBadge) {
    return -1
  }

  return compareBadgeLinesForScoring(
    {
      badge: leftBadge,
      earnedAtLedgerIndex: left.earnedAtLedgerIndex,
      earnedAtMs: left.earnedAtMs,
      points: leftBadge.points,
    },
    {
      badge: rightBadge,
      earnedAtLedgerIndex: right.earnedAtLedgerIndex,
      earnedAtMs: right.earnedAtMs,
      points: rightBadge.points,
    },
  )
}

export type BoggleRunSummaryScore = {
  /** Earned-badge awards for the post-run reveal (rarity ascending, then unlock order). */
  badgeLines: BoggleRunSummaryBadgeLine[]
  coreLines: BoggleRunSummaryCoreLine[]
  rating: BoggleRunSummaryRatingConfig
  stats: {
    averageWordLength: null | number
    lettersPlayed: number
    longestWord: null | string
    wordCount: number
  }
  tierLines: BoggleRunSummaryTierLine[]
  /** Final post-badge total; matches `rating` and `total` historically. */
  total: number
}

export const BOGGLE_RUN_SUMMARY_CORE_LINES = [
  {
    id: 'tiles-cleared',
    label: 'Tiles Cleared',
    pointsPerUnit: 1,
  },
  {
    id: 'gold-tiles-used',
    label: 'Gold Tiles',
    pointsPerUnit: 1,
  },
] as const satisfies readonly BoggleRunSummaryCoreLineConfig[]

export const BOGGLE_RUN_SUMMARY_TIER_CATEGORIES = [
  {
    id: 'wordsmith',
    label: 'Wordsmith',
    metricLabel: 'Words Played',
    tiers: [
      { level: 'I', minInclusive: 10, points: 5 },
      { level: 'II', minInclusive: 20, points: 10 },
      { level: 'III', minInclusive: 30, points: 15 },
      { level: 'IV', minInclusive: 40, points: 20 },
      { level: 'V', minInclusive: 50, points: 25 },
    ],
  },
  {
    id: 'scholar',
    label: 'Scholar',
    meterMinInclusive: 3,
    metricLabel: 'Average Length',
    // Scholar uses "highest qualifying tier": each minimum is inclusive and
    // has no exclusive max. This makes 3.9 tier II, 4.0-4.099 tier II, and
    // 4.1 tier III instead of leaving decimal gaps.
    tiers: [
      { level: 'I', minInclusive: 3.7, points: 5 },
      { level: 'II', minInclusive: 3.9, points: 10 },
      { level: 'III', minInclusive: 4.1, points: 15 },
      { level: 'IV', minInclusive: 4.3, points: 20 },
      { level: 'V', minInclusive: 4.5, points: 25 },
    ],
  },
  {
    id: 'pathfinder',
    label: 'Pathfinder',
    meterMinInclusive: 3,
    metricLabel: 'Longest Word',
    tiers: [
      { level: 'I', minInclusive: 5, points: 5 },
      { level: 'II', minInclusive: 6, points: 10 },
      { level: 'III', minInclusive: 7, points: 15 },
      { level: 'IV', minInclusive: 8, points: 20 },
      { level: 'V', minInclusive: 9, points: 25 },
    ],
  },
] as const satisfies readonly BoggleRunSummaryTierCategoryConfig[]

export const BOGGLE_RUN_SUMMARY_RATINGS = [
  { id: 'none', label: '', minInclusive: 0 },
  { id: 'bronze', label: 'Bronze', minInclusive: 75 },
  { id: 'silver', label: 'Silver', minInclusive: 150 },
  { id: 'gold', label: 'Gold', minInclusive: 230 },
  { id: 'diamond', label: 'Diamond', minInclusive: 290 },
  { id: 'ace', label: 'Ace', minInclusive: 360 },
] as const satisfies readonly BoggleRunSummaryRatingConfig[]

function normalizeNonNegativeInteger(value: number) {
  return Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0
}

export function getUniqueScoredWordCount(scoredWords: readonly string[]) {
  return new Set(scoredWords).size
}

function getAverageWordLength(scoredWords: readonly string[]) {
  if (scoredWords.length === 0) {
    return 0
  }

  return scoredWords.reduce((sum, word) => sum + word.length, 0) / scoredWords.length
}

function getLongestWordLength(scoredWords: readonly string[]) {
  return scoredWords.reduce((longest, word) => Math.max(longest, word.length), 0)
}

function getLongestWord(scoredWords: readonly string[]) {
  let longestWord: null | string = null

  for (const word of scoredWords) {
    if (longestWord === null || word.length > longestWord.length) {
      longestWord = word
    }
  }

  return longestWord
}

function getLettersPlayed(scoredWords: readonly string[]) {
  return scoredWords.reduce((sum, word) => sum + word.length, 0)
}

export function getTierForMetric(
  tiers: readonly BoggleRunSummaryTierConfig[],
  metricValue: number,
) {
  let resolvedTier: BoggleRunSummaryTierConfig | null = null

  for (const tier of tiers) {
    if (metricValue >= tier.minInclusive) {
      resolvedTier = tier
    }
  }

  return resolvedTier
}

function getRatingForTotal(total: number) {
  let rating: BoggleRunSummaryRatingConfig = BOGGLE_RUN_SUMMARY_RATINGS[0]!

  for (const candidate of BOGGLE_RUN_SUMMARY_RATINGS) {
    if (total >= candidate.minInclusive) {
      rating = candidate
    }
  }

  return rating
}

function getTierMetricValue(
  categoryId: BoggleRunSummaryTierCategoryId,
  scoredWords: readonly string[],
) {
  if (categoryId === 'wordsmith') {
    return getUniqueScoredWordCount(scoredWords)
  }

  if (categoryId === 'scholar') {
    return getAverageWordLength(scoredWords)
  }

  return getLongestWordLength(scoredWords)
}

export function getRunSummaryTierLevel(
  categoryId: BoggleRunSummaryTierCategoryId,
  scoredWords: readonly string[],
): BoggleRunSummaryTierLevel | null {
  const category = BOGGLE_RUN_SUMMARY_TIER_CATEGORIES.find(
    candidate => candidate.id === categoryId,
  )
  if (!category) {
    return null
  }
  const metricValue = getTierMetricValue(categoryId, scoredWords)
  return getTierForMetric(category.tiers, metricValue)?.level ?? null
}

export function evaluateRunSummaryScore({
  earnedBadges,
  goldTilesUsed,
  scoredWords,
  tilesCleared,
}: BoggleRunSummaryScoreInput): BoggleRunSummaryScore {
  const coreValues: Record<BoggleRunSummaryCoreLineId, number> = {
    'gold-tiles-used': normalizeNonNegativeInteger(goldTilesUsed),
    'tiles-cleared': normalizeNonNegativeInteger(tilesCleared),
  }
  const coreLines = BOGGLE_RUN_SUMMARY_CORE_LINES.map(line => {
    const value = coreValues[line.id]

    return {
      ...line,
      points: value * line.pointsPerUnit,
      value,
    }
  })
  const tierLines = BOGGLE_RUN_SUMMARY_TIER_CATEGORIES.map(category => {
    const metricValue = getTierMetricValue(category.id, scoredWords)
    const tier = getTierForMetric(category.tiers, metricValue)

    return {
      category,
      metricValue,
      points: tier?.points ?? 0,
      tier,
    }
  })
  const badgeLines = (earnedBadges ?? [])
    .map(earned => {
      const badge = getBoggleBadgeDefinition(earned.badgeId)
      return {
        badge,
        earnedAtLedgerIndex: earned.earnedAtLedgerIndex,
        earnedAtMs: earned.earnedAtMs,
        points: badge.points,
      }
    })
    .sort(compareBadgeLinesForScoring)
  const total =
    coreLines.reduce((sum, line) => sum + line.points, 0) +
    tierLines.reduce((sum, line) => sum + line.points, 0) +
    badgeLines.reduce((sum, line) => sum + line.points, 0)

  return {
    badgeLines,
    coreLines,
    rating: getRatingForTotal(total),
    stats: {
      averageWordLength:
        scoredWords.length === 0 ? null : getAverageWordLength(scoredWords),
      lettersPlayed: getLettersPlayed(scoredWords),
      longestWord: getLongestWord(scoredWords),
      wordCount: getUniqueScoredWordCount(scoredWords),
    },
    tierLines,
    total,
  }
}
